import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { T, F, th as palette } from "@/lib/bricks";
import { Section, Stats, List, Row, Empty, TextLink, figLabel } from "@/components/calm/Calm";
import { toast } from "sonner";
import BricksLoader from "@/components/BricksLoader";
import DeskBoard from "@/components/desk/DeskBoard";

export default function TeamOverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  useEffect(() => {
    api.get("/dashboard/overview")
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:240 }}>
      <BricksLoader variant="assemble" size={40} color={T.brick} />
    </div>
  );
  if (!data) return <DeskBoard />;

  const totalUsed = data.total_used || 0;
  const totalAlloc = data.total_allocation || 1;
  const usedPct = Math.min(100, Math.round((totalUsed / totalAlloc) * 100));
  const cycleDay = data.cycle_day || new Date().getDate();
  const pacePct = Math.round((cycleDay / 30) * 100);
  const overageCost = data.overage_cost || 0;
  const planPrice = data.plan_price || 799;
  const teamUsage = data.team_usage || [];

  const shots = (data.recent_deliveries || []).filter(d => d?.thumbnail_url).slice(0, 6);
  const closedFiles = data.closed_files || data.completed_jobs || 0;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:32, paddingBottom:96 }}>
    <DeskBoard />
    <div data-testid="team-overview-page" style={{ display:'flex', flexDirection:'column', gap:32, fontFamily:F.display, color:p.ink }}>

      <Section title={data.plan_name || 'Team'}
        note={`${data.team_members_count || 0} members, ${totalUsed} shoots used this cycle`}
        action={<TextLink onClick={() => navigate('/team/members')}>Manage team</TextLink>}>
        <Stats testid="team-plan-stats" items={[
          { label: `Shoots used of ${totalAlloc}`, value: String(totalUsed), attention: usedPct > pacePct + 15 },
          { label: 'Remaining', value: String(Math.max(0, totalAlloc - totalUsed)) },
          { label: overageCost > 0 ? `Projected, incl. $${overageCost.toLocaleString()} overage` : 'Projected this month', value: `$${(planPrice + overageCost).toLocaleString()}`, attention: overageCost > 0 },
        ]} />
      </Section>

      <Section title="Member usage">
        {teamUsage.length === 0 ? <Empty>No members yet. Invite your team from Manage team.</Empty> : (
          <List testid="team-member-usage">
            {teamUsage.map((m, i) => {
              const pct = Math.min(100, Math.round((m.shoots_used / Math.max(m.allocation, 1)) * 100));
              const cap = m.shoots_used >= m.allocation;
              return (
                <Row key={i} wide={false} title={m.name || m.email || 'Member'}
                  meta={[m.name ? m.email : null, figLabel(`${m.shoots_used} of ${m.allocation}`, 'shoots')].filter(Boolean)}
                  status={cap ? 'At cap' : `${pct}%`} attention={cap} />
              );
            })}
          </List>
        )}
      </Section>

      <Section title="Recent deliveries" action={<TextLink onClick={() => navigate('/team/properties')}>Media library</TextLink>}>
        {shots.length ? (
          <div className="grid grid-cols-3 sm:grid-cols-6" style={{ gap: 6 }}>
            {shots.map((item, i) => (
              <button key={i} type="button" onClick={() => navigate('/team/properties')} aria-label={item.address || 'Delivery'} style={{ padding:0, border:'none', background:'none', cursor:'pointer' }}>
                <img src={item.thumbnail_url} alt="" style={{ width:'100%', aspectRatio:'4 / 3', objectFit:'cover', borderRadius:2, display:'block' }}/>
              </button>
            ))}
          </div>
        ) : <Empty>No deliveries yet.</Empty>}
        {closedFiles > 0 && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: p.ink3 }}>
            <TextLink onClick={() => navigate('/team/properties?filter=closed')}>{closedFiles} closed listing{closedFiles !== 1 ? 's' : ''}, still live</TextLink>
          </div>
        )}
      </Section>
    </div>
    </div>
  );
}
